import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, FileText } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MyAssignmentsView } from "@/routes/_authenticated/documents";
import { AssignedCertsBanner, type MyCertAssignment } from "@/routes/_authenticated/my-certifications";

type AssignmentsTab = "docs" | "certs";

export const Route = createFileRoute("/_authenticated/my-assignments")({
  validateSearch: (s: Record<string, unknown>): { tab?: AssignmentsTab } =>
    s.tab === "docs" || s.tab === "certs" ? { tab: s.tab } : {},
  component: MyAssignmentsPage,
});

/** Everything assigned to the signed-in worker, one tab per kind of assignment. */
function MyAssignmentsPage() {
  const { tab = "docs" } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: certData } = useQuery({
    queryKey: ["my-cert-assignments"],
    queryFn: () => apiGet<{ data: MyCertAssignment[] }>("/api/certifications/my-assignments"),
    staleTime: 30_000,
  });
  const { data: docData } = useQuery({
    queryKey: ["my-doc-assignments"],
    queryFn: () => apiGet<{ data: { completionStatus: string }[] }>("/api/doc-assignments/mine"),
    staleTime: 30_000,
  });
  const certs = (certData?.data ?? []).filter((a) => a.status !== "approved");
  const docCount = (docData?.data ?? []).filter((a) => a.completionStatus !== "approved").length;

  const tabs: { id: AssignmentsTab; label: string; icon: typeof FileText; count: number }[] = [
    { id: "docs", label: "Documents", icon: FileText, count: docCount },
    { id: "certs", label: "Credentials", icon: Award, count: certs.length },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-4 pb-24">
      <h1 className="text-2xl font-bold text-text-primary pt-2">My Assignments</h1>

      <div className="flex rounded-lg border border-border bg-muted/40 p-1 gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => navigate({ to: "/my-assignments", search: { tab: t.id }, replace: true })}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              tab === t.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {t.count > 0 && (
              <span className="ml-0.5 min-w-[18px] rounded-full bg-paris-red px-1.5 text-[10px] font-bold leading-[18px] text-white">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "docs" ? (
        <MyAssignmentsView />
      ) : certs.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No credentials assigned to you right now.</p>
      ) : (
        <AssignedCertsBanner
          assignments={certs}
          onUpload={(certTypeId) => navigate({ to: "/my-certifications", search: { upload: certTypeId } })}
          onClaimed={() => qc.invalidateQueries({ queryKey: ["my-cert-assignments"] })}
        />
      )}
    </div>
  );
}
